import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import { isNullOrEmpty } from '../util/Util';

export const CONTACT_CLAIMS = {
    contact_email: 'contact_email',
    contact_email_verified: 'contact_email_verified',
    contact_phone_number: 'contact_phone_number',
    contact_id: 'contact_id',
    contact_link_state: 'contact_link_state',
    contact_address: 'contact_address',
    hire_date: 'hire_date',
};

const CLAIM_TYPE_NAMESPACE = 'http://www.w3.org/2001/XMLSchema';

function findContactClaim(userClaims, claimType, type = undefined) {
    const claim = userClaims?.find(
        clm => clm.claimType === claimType && ((type != null && clm.claimValue.type === type) || type == null)
    );
    return claim?.claimValue?.value ?? claim?.claimValue ?? '';
}

function findAddressClaim(userClaims, claimType) {
    const claim = userClaims?.find(clm => clm.claimType === claimType);
    return claim?.claimValue ?? '';
}

function createContactClaim(claimType, type, value) {
    const claim = {
        claimType,
        claimValue: {
            value,
            type,
        },
        claimValueType: 'json',
    };
    return claim;
}

function createAddressClaim(claimType, claimValue) {
    const claim = {
        claimType,
        claimValue,
        claimValueType: 'json',
    };
    return claim;
}

function createHireDateClaim(claimType, claimValue) {
    const claim = {
        claimType,
        claimValue: {
            value: claimValue,
            type: claimType,
        },
        claimValueType: 'json',
    };
    return claim;
}

function parseContactPhoneNumber(phoneNumberString) {
    const phoneParts = phoneNumberString.split(';ext=');
    return {
        number: phoneParts[0],
        ext: phoneParts.length > 1 ? phoneParts[1] : undefined,
    };
}

function formatContactPhoneNumber(number, ext) {
    if (!ext) {
        return `${number}`;
    }
    return `${number};ext=${ext}`;
}

function createOrUpdateContactClaim(userClaims, claimType, type, value) {
    let claim;
    let position;

    const idx = userClaims.findIndex(e => e.claimType === claimType && e.claimValue.type === type);
    if (idx > -1) {
        claim = userClaims[idx];
        claim.claimValue.value = value;
        position = idx.toString();
    } else {
        claim = createContactClaim(claimType, type, value);
        position = '-';
    }

    return [position, claim];
}

function createOrUpdateAddressClaim(userClaims, claimType, value) {
    let claim;
    let position;

    const idx = userClaims.findIndex(e => e.claimType === claimType);
    if (idx > -1) {
        claim = userClaims[idx];
        claim.claimValue = value;
        position = idx.toString();
    } else {
        claim = createAddressClaim(claimType, value);
        position = '-';
    }

    return [position, claim];
}

const trimHireDate = hireDate => hireDate.replace(/T.*$/, '') || '';

export function getAdditionalClaimPatchesFromMatch(match, currentUserClaims) {
    const getAddOrReplacePatch = (index = '-', value) => {
        const resolvedIndex = typeof index === 'number' && index >= 0 ? index : '-';
        return {
            path: `additionalClaims/${resolvedIndex}`,
            op: resolvedIndex === '-' ? 'add' : 'replace',
            value,
        };
    };

    const getIndexOfClaimType = (claimType, type) =>
        currentUserClaims.findIndex(
            claim => claim.claimType === claimType && (type == null || claim.claimValue?.type === type)
        );

    const patches = [];

    patches.push(
        getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_id), {
            claimType: CONTACT_CLAIMS.contact_id,
            claimValue: match.id,
            claimValueType: `${CLAIM_TYPE_NAMESPACE}#string`,
        })
    );

    patches.push(
        getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_link_state), {
            claimType: CONTACT_CLAIMS.contact_link_state,
            claimValue: 'Linked',
            claimValueType: `${CLAIM_TYPE_NAMESPACE}#string`,
        })
    );

    patches.push(
        getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_email), {
            claimType: CONTACT_CLAIMS.contact_email,
            claimValue: JSON.stringify({
                value: match.email,
                type: 'contact',
            }),
            claimValueType: 'json',
        })
    );

    if (match.phoneNumber.direct) {
        patches.push(
            getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_phone_number, 'direct'), {
                claimType: CONTACT_CLAIMS.contact_phone_number,
                claimValue: JSON.stringify({
                    value: match.phoneNumber.direct,
                    type: 'direct',
                }),
                claimValueType: 'json',
            })
        );
    }

    if (match.phoneNumber.mobile) {
        patches.push(
            getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_phone_number, 'mobile'), {
                claimType: CONTACT_CLAIMS.contact_phone_number,
                claimValue: JSON.stringify({
                    value: match.phoneNumber.mobile,
                    type: 'mobile',
                }),
                claimValueType: 'json',
            })
        );
    }

    if (match.phoneNumber.preferred) {
        patches.push(
            getAddOrReplacePatch(getIndexOfClaimType(CONTACT_CLAIMS.contact_phone_number, 'preferred'), {
                claimType: CONTACT_CLAIMS.contact_phone_number,
                claimValue: JSON.stringify({
                    value: match.phoneNumber.preferred,
                    type: 'preferred',
                }),
                claimValueType: 'json',
            })
        );
    }

    return patches;
}

export function getUserContactInfoFromClaims(userClaims) {
    if (userClaims === null || userClaims?.length === 0) {
        return {};
    }

    const contactIdClaimValue = findContactClaim(userClaims, CONTACT_CLAIMS.contact_id);
    const contactEmailVerifiedClaimValue = findContactClaim(userClaims, CONTACT_CLAIMS.contact_email_verified);
    const contactEmailClaimValue = findContactClaim(userClaims, CONTACT_CLAIMS.contact_email, 'contact');
    const contactDirectNumberClaimValue = parseContactPhoneNumber(
        findContactClaim(userClaims, CONTACT_CLAIMS.contact_phone_number, 'direct')
    );
    const contactMobileNumberClaimValue = parseContactPhoneNumber(
        findContactClaim(userClaims, CONTACT_CLAIMS.contact_phone_number, 'mobile')
    );
    const contactPreferredNumberClaimValue = parseContactPhoneNumber(
        findContactClaim(userClaims, CONTACT_CLAIMS.contact_phone_number, 'preferred')
    );
    const addressClaimValue = findAddressClaim(userClaims, CONTACT_CLAIMS.contact_address);
    const hireDateClaimValue = findContactClaim(userClaims, CONTACT_CLAIMS.hire_date);

    return {
        contactId: contactIdClaimValue,
        contactEmail: contactEmailClaimValue,
        contactEmailVerified: contactEmailVerifiedClaimValue,
        directNumber: contactDirectNumberClaimValue.number,
        directNumberExt: contactDirectNumberClaimValue.ext,
        mobileNumber: contactMobileNumberClaimValue.number,
        mobileNumberExt: contactMobileNumberClaimValue.ext,
        preferredNumber: contactPreferredNumberClaimValue.number,
        preferredNumberExt: contactPreferredNumberClaimValue.ext,
        addressInfo: addressClaimValue,
        hireDate: hireDateClaimValue,
    };
}

export function getAdditionalClaimsToRemove(user) {
    const { contactInfo } = user;

    if (contactInfo === null) {
        return [];
    }

    const patches = [];

    if (isNullOrEmpty(contactInfo.contactEmail)) {
        const idx = user.additionalClaims.findIndex(
            e => e.claimType === CONTACT_CLAIMS.contact_email && e.claimValue.type === 'contact'
        );
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
    }

    if (isNullOrEmpty(contactInfo.directNumber)) {
        const idx = user.additionalClaims.findIndex(
            e => e.claimType === CONTACT_CLAIMS.contact_phone_number && e.claimValue.type === 'direct'
        );
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
    }
    if (isNullOrEmpty(contactInfo.mobileNumber)) {
        const idx = user.additionalClaims.findIndex(
            e => e.claimType === CONTACT_CLAIMS.contact_phone_number && e.claimValue.type === 'mobile'
        );
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
    }

    if (isNullOrEmpty(contactInfo.preferredNumber)) {
        const idx = user.additionalClaims.findIndex(
            e => e.claimType === CONTACT_CLAIMS.contact_phone_number && e.claimValue.type === 'preferred'
        );
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
    }

    if (isNullOrEmpty(contactInfo.hireDate)) {
        const idx = user.additionalClaims.findIndex(
            e => e.claimType === CONTACT_CLAIMS.hire_date && e.claimValue.type === 'hire_date'
        );
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
    }

    if (isNullOrEmpty(contactInfo.addressInfo) || isEmpty(Object.keys(contactInfo.addressInfo))) {
        const idx = user.additionalClaims.findIndex(e => e.claimType === CONTACT_CLAIMS.contact_address);
        if (idx >= 0) {
            patches.push({
                path: `additionalClaims/${idx}`,
                op: 'remove',
            });
        }
        if (isEmpty(Object.keys(contactInfo.addressInfo))) {
            user.additionalClaims.splice(idx, 1);
        }
    }

    return patches;
}

export function updateContactInfoClaim(user, contactInfo) {
    const userClaims = user.additionalClaims;
    const currentContactInfo = user.contactInfo;
    if (contactInfo === null) {
        return [];
    }
    const updatedClaims = [];

    if (!isNullOrEmpty(contactInfo.contactEmail)) {
        const claim = createOrUpdateContactClaim(
            userClaims,
            CONTACT_CLAIMS.contact_email,
            'contact',
            contactInfo.contactEmail
        );
        updatedClaims.push(claim);
    }

    if (
        !isNullOrEmpty(contactInfo.directNumber) ||
        !isNullOrEmpty(currentContactInfo.directNumber) ||
        !isNullOrEmpty(contactInfo.directNumberExt)
    ) {
        const number = contactInfo.directNumber ? contactInfo.directNumber : currentContactInfo.directNumber;
        const ext = contactInfo.directNumberExt ? contactInfo.directNumberExt : currentContactInfo.directNumberExt;
        const phoneNumber = formatContactPhoneNumber(number, ext);
        const claim = createOrUpdateContactClaim(
            userClaims,
            CONTACT_CLAIMS.contact_phone_number,
            'direct',
            phoneNumber
        );
        updatedClaims.push(claim);
    }

    if (
        !isNullOrEmpty(contactInfo.mobileNumber) ||
        !isNullOrEmpty(currentContactInfo.mobileNumber) ||
        !isNullOrEmpty(contactInfo.mobileNumberExt)
    ) {
        const number = contactInfo.mobileNumber ? contactInfo.mobileNumber : currentContactInfo.mobileNumber;
        const ext = contactInfo.mobileNumberExt ? contactInfo.mobileNumberExt : currentContactInfo.mobileNumberExt;
        const phoneNumber = formatContactPhoneNumber(number, ext);
        const claim = createOrUpdateContactClaim(
            userClaims,
            CONTACT_CLAIMS.contact_phone_number,
            'mobile',
            phoneNumber
        );
        updatedClaims.push(claim);
    }

    if (
        !isNullOrEmpty(contactInfo.preferredNumber) ||
        !isNullOrEmpty(currentContactInfo.preferredNumber) ||
        !isNullOrEmpty(contactInfo.preferredNumberExt)
    ) {
        const number = contactInfo.preferredNumber ? contactInfo.preferredNumber : currentContactInfo.preferredNumber;
        const ext = contactInfo.preferredNumberExt
            ? contactInfo.preferredNumberExt
            : currentContactInfo.preferredNumberExt;
        const phoneNumber = formatContactPhoneNumber(number, ext);
        const claim = createOrUpdateContactClaim(
            userClaims,
            CONTACT_CLAIMS.contact_phone_number,
            'preferred',
            phoneNumber
        );
        updatedClaims.push(claim);
    }

    if (!isNullOrEmpty(user?.contactInfo?.addressInfo)) {
        const { addressInfo = {} } = user?.contactInfo;
        forEach(Object.keys(addressInfo), key => {
            if (!addressInfo[key]) delete addressInfo[key];
        });
        if (!isEmpty(addressInfo)) {
            const claim = createOrUpdateAddressClaim(userClaims, CONTACT_CLAIMS.contact_address, addressInfo);
            updatedClaims.push(claim);
        }
    }

    if (!isNullOrEmpty(user?.contactInfo?.hireDate)) {
        const hireDate = get(user, 'contactInfo.hireDate') || {};
        const claim = createOrUpdateContactClaim(
            userClaims,
            CONTACT_CLAIMS.hire_date,
            'hire_date',
            trimHireDate(hireDate)
        );
        updatedClaims.push(claim);
    }

    return updatedClaims;
}

export function createAdditionalClaims(contactInfo) {
    if (contactInfo === null) {
        return [];
    }
    const claims = [];

    if (!isNullOrEmpty(contactInfo.contactEmail)) {
        const claim = createContactClaim(CONTACT_CLAIMS.contact_email, 'contact', contactInfo.contactEmail);
        claims.push(claim);
    }

    if (!isNullOrEmpty(contactInfo.directNumber) || !isNullOrEmpty(contactInfo.directNumberExt)) {
        const phoneNumber = formatContactPhoneNumber(contactInfo.directNumber, contactInfo.directNumberExt);
        const claim = createContactClaim(CONTACT_CLAIMS.contact_phone_number, 'direct', phoneNumber);
        claims.push(claim);
    }

    if (!isNullOrEmpty(contactInfo.mobileNumber) || !isNullOrEmpty(contactInfo.mobileNumberExt)) {
        const phoneNumber = formatContactPhoneNumber(contactInfo.mobileNumber, contactInfo.mobileNumberExt);
        const claim = createContactClaim(CONTACT_CLAIMS.contact_phone_number, 'mobile', phoneNumber);
        claims.push(claim);
    }

    if (!isNullOrEmpty(contactInfo.preferredNumber) || !isNullOrEmpty(contactInfo.preferredNumberExt)) {
        const phoneNumber = formatContactPhoneNumber(contactInfo.preferredNumber, contactInfo.preferredNumberExt);
        const claim = createContactClaim(CONTACT_CLAIMS.contact_phone_number, 'preferred', phoneNumber);
        claims.push(claim);
    }
    if (!isNullOrEmpty(contactInfo.addressInfo)) {
        const claim = createAddressClaim(CONTACT_CLAIMS.contact_address, contactInfo.addressInfo);
        claims.push(claim);
    }
    if (!isNullOrEmpty(contactInfo.hireDate)) {
        const { hireDate = '' } = contactInfo;
        const claim = createHireDateClaim(CONTACT_CLAIMS.hire_date, trimHireDate(hireDate));
        claims.push(claim);
    }
    return claims;
}

export function initAddressFields() {
    return {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
    };
}
